import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { esInmobiliaria, PerfilInmobiliario, InteresInmobiliario, ETIQUETAS_INMO } from '../components/clientes/FichaInmobiliaria';

const CATEGORIAS = {
  observacion: { label: 'Observación', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
  preferencia: { label: 'Preferencia', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  alergia:     { label: 'Alergia',     color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  medica:      { label: 'Clínica',     color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
};

const ESTADO_BADGE = {
  pendiente:  { label: 'Pendiente',  color: '#d97706', bg: '#fef3c7' },
  confirmada: { label: 'Confirmada', color: '#2563eb', bg: '#dbeafe' },
  completada: { label: 'Completada', color: '#16a34a', bg: '#dcfce7' },
  cancelada:  { label: 'Cancelada',  color: '#dc2626', bg: '#fee2e2' },
  no_asistio: { label: 'No asistió', color: '#9ca3af', bg: '#f3f4f6' },
};

const inputSt = {
  border: '1.5px solid #e5e7eb',
  borderRadius: '8px',
  padding: '0.6rem 0.75rem',
  fontSize: '0.9rem',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: '#fafafa',
  outline: 'none',
};

function Label({ text, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.3rem' }}>
        {text}
      </label>
      {children}
    </div>
  );
}

export default function FichaCliente() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tab,     setTab]     = useState('perfil');
  const [cliente, setCliente] = useState(null);
  const [historial, setHistorial] = useState({ citas: [], stats: {} });
  const [notas,   setNotas]   = useState([]);
  const [loading, setLoading] = useState(true);

  const [perfil,          setPerfil]          = useState({});
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);
  const [inmo,  setInmo]  = useState(false);
  const [ficha, setFicha] = useState({});

  const [nuevaNota,     setNuevaNota]     = useState({ categoria: 'observacion', titulo: '', contenido: '' });
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [cliRes, histRes, notasRes, inmoRes] = await Promise.all([
        api.get(`/clientes/${id}`),
        api.get(`/clientes/${id}/historial`),
        api.get(`/notas?cliente_id=${id}`),
        api.get(`/clientes/${id}/ficha-inmobiliaria`).catch(() => null),
      ]);
      if (inmoRes && esInmobiliaria(inmoRes.data.tipo_negocio)) {
        setInmo(true);
        setFicha(inmoRes.data.ficha || {});
      }
      const c = cliRes.data;
      setCliente(c);
      setPerfil({
        nombres:                c.nombres || '',
        apellidos:              c.apellidos || '',
        identificacion:         c.identificacion || '',
        whatsapp_number:        c.whatsapp_number || '',
        email:                  c.email || '',
        direccion:              c.direccion || '',
        ciudad:                 c.ciudad || '',
        fecha_nacimiento:      c.fecha_nacimiento?.substring(0, 10) || '',
        genero:                 c.genero || 'no_especificado',
        alergias:               c.alergias || '',
        preferencias:           c.preferencias || '',
        observaciones_generales: c.observaciones_generales || '',
      });
      setHistorial(histRes.data);
      setNotas(notasRes.data);
    } catch {
      toast.error('Error al cargar la ficha del cliente');
      navigate('/clientes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarPerfil = async () => {
    if (!perfil.nombres?.trim() || !perfil.apellidos?.trim()) {
      toast.error('Nombre y apellido son requeridos');
      return;
    }
    setGuardandoPerfil(true);
    try {
      await api.put(`/clientes/${id}`, perfil);
      if (inmo) await api.put(`/clientes/${id}/ficha-inmobiliaria`, ficha);
      setCliente(c => ({ ...c, ...perfil }));
      toast.success('Ficha actualizada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setGuardandoPerfil(false);
    }
  };

  const crearNota = async () => {
    if (!nuevaNota.contenido.trim()) { toast.error('El contenido es requerido'); return; }
    setGuardandoNota(true);
    try {
      await api.post('/notas', { ...nuevaNota, cliente_id: parseInt(id) });
      setNuevaNota({ categoria: 'observacion', titulo: '', contenido: '' });
      const { data } = await api.get(`/notas?cliente_id=${id}`);
      setNotas(data);
      toast.success('Nota añadida');
    } catch {
      toast.error('Error al guardar nota');
    } finally {
      setGuardandoNota(false);
    }
  };

  const eliminarNota = (notaId) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/notas/${notaId}`);
        setNotas(n => n.filter(x => x.id !== notaId));
      } catch {
        toast.error('Error al eliminar');
      }
    }});
  };

  if (loading) return (
    <Layout>
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="loading" />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Cargando ficha...</p>
      </div>
    </Layout>
  );

  const iniciales = `${cliente.nombres?.[0] || ''}${cliente.apellidos?.[0] || ''}`.toUpperCase();
  const { stats, citas } = historial;

  return (
    <Layout>

      {/* Breadcrumb */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
        <button
          onClick={() => navigate('/clientes')}
          style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.875rem', padding: 0, fontWeight: '600' }}
        >
          ← Clientes
        </button>
        <span>/</span>
        <span style={{ color: '#111827', fontWeight: '600' }}>{cliente.nombres} {cliente.apellidos}</span>
      </div>

      {/* Header */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          <div style={{
            width: '4rem', height: '4rem', borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff', fontWeight: '700', fontSize: '1.4rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {iniciales}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: '800', color: '#111827' }}>
              {cliente.nombres} {cliente.apellidos}
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.85rem', marginTop: '0.4rem' }}>
              {cliente.whatsapp_number && <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>📞 {cliente.whatsapp_number}</span>}
              {cliente.email          && <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>✉️ {cliente.email}</span>}
              {cliente.identificacion && <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>🪪 {cliente.identificacion}</span>}
              {inmo && (cliente.direccion || cliente.ciudad) && (
                <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>📍 {[cliente.ciudad, cliente.direccion].filter(Boolean).join(' · ')}</span>
              )}
            </div>
            {inmo && (() => {
              const chips = [
                ficha.estado_civil && { txt: ETIQUETAS_INMO.estado_civil[ficha.estado_civil], color: '#6d28d9', bg: '#f5f3ff' },
                (ficha.interes_tipo_inmueble || ficha.interes_operacion) && {
                  txt: `Busca: ${[ETIQUETAS_INMO.interes_tipo_inmueble[ficha.interes_tipo_inmueble], ETIQUETAS_INMO.interes_operacion[ficha.interes_operacion]].filter(Boolean).join(' · ')}`,
                  color: '#1d4ed8', bg: '#eff6ff',
                },
                ficha.presupuesto && { txt: `Presupuesto $${Number(ficha.presupuesto).toLocaleString('es-EC')}`, color: '#047857', bg: '#ecfdf5' },
                ficha.forma_pago && { txt: ETIQUETAS_INMO.forma_pago[ficha.forma_pago], color: '#b45309', bg: '#fffbeb' },
              ].filter(Boolean);
              return chips.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.6rem' }}>
                  {chips.map(ch => (
                    <span key={ch.txt} style={{ fontSize: '0.75rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '12px', background: ch.bg, color: ch.color }}>
                      {ch.txt}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '1.75rem', flexShrink: 0 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#111827', lineHeight: 1 }}>{stats.total_visitas || 0}</div>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.2rem' }}>Visitas</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#16a34a', lineHeight: 1 }}>{stats.completadas || 0}</div>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.2rem' }}>Completadas</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1d4ed8', lineHeight: 1 }}>
                ${parseFloat(stats.total_gastado || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.2rem' }}>Total</div>
            </div>
          </div>
        </div>

        {/* Alertas de la ficha */}
        {!inmo && (cliente.alergias || cliente.preferencias) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f3f4f6' }}>
            {cliente.alergias && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#dc2626', maxWidth: '100%' }}>
                <span>⚠️</span>
                <span><strong>Alergias:</strong> {cliente.alergias}</span>
              </div>
            )}
            {cliente.preferencias && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#1d4ed8', maxWidth: '100%' }}>
                <span>💜</span>
                <span><strong>Preferencias:</strong> {cliente.preferencias}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '1.25rem', overflowX: 'auto', overflowY: 'hidden' }}>
        {[
          { key: 'perfil',    label: inmo ? '👤 Ficha del cliente' : '👤 Perfil' },
          inmo && { key: 'interes', label: '🏠 Interés inmobiliario' },
          { key: 'historial', label: `📅 Historial (${citas.length})` },
          { key: 'notas',     label: `📝 Notas (${notas.length})` },
        ].filter(Boolean).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.75rem 1.25rem', fontSize: '0.9rem', whiteSpace: 'nowrap', flexShrink: 0,
              fontWeight: tab === t.key ? '700' : '500',
              color: tab === t.key ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ── Perfil ─────────────────────────────────────────────────────────── */}
      {inmo && (tab === 'perfil' || tab === 'interes') && (
        <div className="card">
          {tab === 'perfil'
            ? <PerfilInmobiliario perfil={perfil} setPerfil={setPerfil} ficha={ficha} setFicha={setFicha} inputSt={inputSt} Label={Label} />
            : <InteresInmobiliario ficha={ficha} setFicha={setFicha} inputSt={inputSt} Label={Label} />}
          <button
            className="btn btn-primary"
            onClick={guardarPerfil}
            disabled={guardandoPerfil}
            style={{ opacity: guardandoPerfil ? 0.7 : 1 }}
          >
            {guardandoPerfil ? 'Guardando...' : '💾 Guardar ficha'}
          </button>
        </div>
      )}

      {!inmo && tab === 'perfil' && (
        <div className="card">
          <h3 style={{ marginTop: 0, marginBottom: '1.25rem', fontSize: '1rem', fontWeight: '700', color: '#374151' }}>
            Información de contacto
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <Label text="Nombres *">
              <input style={inputSt} value={perfil.nombres} onChange={e => setPerfil(p => ({...p, nombres: e.target.value}))} />
            </Label>
            <Label text="Apellidos *">
              <input style={inputSt} value={perfil.apellidos} onChange={e => setPerfil(p => ({...p, apellidos: e.target.value}))} />
            </Label>
            <Label text="WhatsApp">
              <input style={inputSt} type="tel" value={perfil.whatsapp_number} onChange={e => setPerfil(p => ({...p, whatsapp_number: e.target.value}))} placeholder="0991234567" />
            </Label>
            <Label text="Email">
              <input style={inputSt} type="email" value={perfil.email} onChange={e => setPerfil(p => ({...p, email: e.target.value}))} placeholder="cliente@email.com" />
            </Label>
            <Label text="C.I / RUC">
              <input style={inputSt} value={perfil.identificacion} onChange={e => setPerfil(p => ({...p, identificacion: e.target.value}))} />
            </Label>
          </div>

          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1.25rem', marginBottom: '1.25rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.25rem', fontSize: '1rem', fontWeight: '700', color: '#374151' }}>
              Ficha clínica / Preferencias
            </h3>
            <div className="grid-responsive" style={{ gap: '1rem', marginBottom: '1rem' }}>
              <Label text="Fecha de nacimiento">
                <input style={inputSt} type="date" value={perfil.fecha_nacimiento} onChange={e => setPerfil(p => ({...p, fecha_nacimiento: e.target.value}))} />
              </Label>
              <Label text="Género">
                <select style={inputSt} value={perfil.genero} onChange={e => setPerfil(p => ({...p, genero: e.target.value}))}>
                  <option value="no_especificado">No especificado</option>
                  <option value="femenino">Femenino</option>
                  <option value="masculino">Masculino</option>
                  <option value="otro">Otro</option>
                </select>
              </Label>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <Label text="⚠️ Alergias / Contraindicaciones">
                <textarea
                  style={{ ...inputSt, resize: 'vertical' }} rows={3}
                  value={perfil.alergias}
                  onChange={e => setPerfil(p => ({...p, alergias: e.target.value}))}
                  placeholder="Ej: alérgica al látex, no soporta tinte con amoniaco..."
                />
              </Label>
              <Label text="💜 Preferencias del cliente">
                <textarea
                  style={{ ...inputSt, resize: 'vertical' }} rows={3}
                  value={perfil.preferencias}
                  onChange={e => setPerfil(p => ({...p, preferencias: e.target.value}))}
                  placeholder="Ej: prefiere productos sin sulfatos, le gusta música tranquila..."
                />
              </Label>
              <Label text="📋 Observaciones generales">
                <textarea
                  style={{ ...inputSt, resize: 'vertical' }} rows={3}
                  value={perfil.observaciones_generales}
                  onChange={e => setPerfil(p => ({...p, observaciones_generales: e.target.value}))}
                  placeholder="Cualquier información adicional relevante..."
                />
              </Label>
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={guardarPerfil}
            disabled={guardandoPerfil}
            style={{ opacity: guardandoPerfil ? 0.7 : 1 }}
          >
            {guardandoPerfil ? 'Guardando...' : '💾 Guardar ficha'}
          </button>
        </div>
      )}

      {/* ── Historial ───────────────────────────────────────────────────────── */}
      {tab === 'historial' && (
        <div className="card">
          {citas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              <span style={{ fontSize: '3rem' }}>📅</span>
              <p style={{ marginTop: '1rem' }}>Este cliente no tiene citas registradas</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Hora</th>
                    <th>Servicio</th>
                    <th>Duración</th>
                    <th>Precio</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {citas.map(c => {
                    const est = ESTADO_BADGE[c.estado] || ESTADO_BADGE.pendiente;
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight: '600' }}>{c.fecha_cita?.substring(0, 10)}</td>
                        <td>{c.hora_inicio?.substring(0, 5)}</td>
                        <td>{c.servicio_nombre || '—'}</td>
                        <td style={{ color: '#6b7280' }}>{c.duracion_minutos ? `${c.duracion_minutos} min` : '—'}</td>
                        <td style={{ color: '#16a34a', fontWeight: '600' }}>
                          {c.precio ? `$${parseFloat(c.precio).toFixed(2)}` : '—'}
                        </td>
                        <td>
                          <span style={{
                            fontSize: '0.78rem', fontWeight: '600',
                            padding: '0.2rem 0.65rem', borderRadius: '12px',
                            background: est.bg, color: est.color,
                          }}>
                            {est.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Notas ───────────────────────────────────────────────────────────── */}
      {tab === 'notas' && (
        <div>
          {/* Formulario nueva nota */}
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: '700', color: '#374151' }}>
              Nueva nota
            </h3>
            <div className="grid-responsive" style={{ gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Label text="Categoría">
                <select style={inputSt} value={nuevaNota.categoria} onChange={e => setNuevaNota(n => ({...n, categoria: e.target.value}))}>
                  {Object.entries(CATEGORIAS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </Label>
              <Label text="Título (opcional)">
                <input style={inputSt} value={nuevaNota.titulo} onChange={e => setNuevaNota(n => ({...n, titulo: e.target.value}))} placeholder="Ej: Sesión 3 — resultado..." />
              </Label>
            </div>
            <Label text="Contenido *">
              <textarea
                style={{ ...inputSt, resize: 'vertical' }} rows={3}
                value={nuevaNota.contenido}
                onChange={e => setNuevaNota(n => ({...n, contenido: e.target.value}))}
                placeholder="Escribe la nota aquí..."
              />
            </Label>
            <button
              className="btn btn-primary"
              style={{ marginTop: '0.75rem', opacity: guardandoNota ? 0.7 : 1 }}
              onClick={crearNota}
              disabled={guardandoNota}
            >
              {guardandoNota ? 'Guardando...' : '+ Añadir nota'}
            </button>
          </div>

          {/* Lista de notas */}
          {notas.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2.5rem', color: '#9ca3af' }}>
              <span style={{ fontSize: '2.5rem' }}>📝</span>
              <p style={{ marginTop: '0.75rem' }}>No hay notas para este cliente. Añade la primera arriba.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {notas.map(nota => {
                const cat = CATEGORIAS[nota.categoria] || CATEGORIAS.observacion;
                return (
                  <div key={nota.id} style={{ background: cat.bg, border: `1px solid ${cat.border}`, borderRadius: '10px', padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '700', padding: '0.15rem 0.55rem', borderRadius: '10px', background: cat.color, color: '#fff' }}>
                          {cat.label}
                        </span>
                        {nota.titulo && (
                          <span style={{ fontWeight: '700', color: '#111827', fontSize: '0.9rem' }}>{nota.titulo}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                          {new Date(nota.created_at).toLocaleDateString('es-EC')} · {nota.usuario_nombre}
                        </span>
                        <button
                          onClick={() => eliminarNota(nota.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.9rem', padding: '0 0.2rem', lineHeight: 1 }}
                          title="Eliminar nota"
                        >✕</button>
                      </div>
                    </div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                      {nota.contenido}
                    </p>
                    {nota.servicio_nombre && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#6b7280' }}>
                        Cita: {nota.servicio_nombre} · {nota.fecha_cita?.substring(0, 10)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
